/**
 * ============================================================================
 * TagList Component - UNIFIED TAG DISPLAY
 * ============================================================================
 * 
 * TAG ENGINE FINAL LOCK:
 * - ProductCard et ProductDetail DOIVENT utiliser ce composant
 * - Utilise getNormalizedTags() pour garantir UI 100% unifiée
 * - Même rendu partout, seul le variant change le style
 * 
 * INTERDIT:
 * - Badges conditionnels hardcodés (ex: if(nouveaute) render badge)
 * - Logique différente entre pages
 * - Mapping UI inline dans les composants parents
 * 
 * ARCHITECTURE TAGS:
 * - Backend (normalize_product) = SOURCE UNIQUE DE VÉRITÉ
 * - Backend filtre 'date_sortie_inconnue' (géré par DateAvailabilityBadge)
 * - Frontend conserve un filtrage soft par sécurité (données legacy)
 */

// ============================================================================
// TAGS EXCLUS - Sécurité soft pour données legacy (backend filtre à la source)
// ============================================================================
const EXCLUDED_TAGS = ['date_sortie_inconnue'];

import React from "react";
import { 
  getNormalizedTags, 
  getTagLabel, 
  getTagStyleDetail, 
  getTagStyleCard,
  isSystemTag,
  isManualTag 
} from "../lib/tagEngine";

/**
 * TagList Component - RENDER UNIQUE
 * 
 * @param {Object} props
 * @param {Object|string[]} props.tags - Produit complet OU array de tags
 * @param {string} props.variant - "card" | "detail"
 * @param {number} props.maxVisible - Max tags visibles (card only, default: 3)
 * @param {boolean} props.showType - Afficher le type de tag (AUTO/MANUAL) - debug only
 * @param {string} props.className - Classes CSS additionnelles
 */
const TagList = ({ 
  tags = [], 
  variant = "card", 
  maxVisible = 3,
  showType = false,
  className = ""
}) => {
  // ============================================================================
  // NORMALISATION - Utilise getNormalizedTags pour UI unifiée
  // ============================================================================
  
  let tagArray = [];
  
  // Si on reçoit un objet produit complet
  if (tags && typeof tags === "object" && !Array.isArray(tags)) {
    tagArray = getNormalizedTags(tags);
  }
  // Si on reçoit un array de tags directement  
  else if (Array.isArray(tags)) {
    tagArray = tags.filter(t => t && typeof t === "string");
  }

  // ============================================================================
  // FILTRAGE SOFT - Sécurité pour données legacy (backend est source de vérité)
  // ============================================================================
  tagArray = tagArray.filter(tag => !EXCLUDED_TAGS.includes(tag));

  // Pas de tags = pas de rendu
  if (!tagArray.length) return null;

  // ============================================================================
  // LIMITATION (card variant uniquement)
  // ============================================================================
  
  const displayTags = variant === "card" ? tagArray.slice(0, maxVisible) : tagArray;
  const remainingCount = variant === "card" ? Math.max(0, tagArray.length - maxVisible) : 0;

  // ============================================================================
  // RENDER UNIQUE
  // ============================================================================
  
  const getStyle = (tag) => {
    return variant === "card" ? getTagStyleCard(tag) : getTagStyleDetail(tag);
  };

  return (
    <div 
      className={`flex flex-wrap gap-1.5 ${className}`}
      data-testid={`tag-list-${variant}`}
    >
      {displayTags.map((tag, idx) => (
        <span
          key={`${tag}-${idx}`}
          className={`
            px-2 py-1 text-xs font-medium rounded-md border
            ${getStyle(tag)}
          `}
          data-testid={`tag-${tag}`}
          data-tag-type={showType ? (isSystemTag(tag) ? "auto" : isManualTag(tag) ? "manual" : "unknown") : undefined}
        >
          {getTagLabel(tag)}
        </span>
      ))}
      
      {/* Indicateur tags supplémentaires */}
      {remainingCount > 0 && (
        <span 
          className="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-600 border border-gray-200"
          data-testid="tag-overflow"
          title={`${remainingCount} autres tags`}
        >
          +{remainingCount}
        </span>
      )}
    </div>
  );
};

/**
 * TagBadge - Badge individuel pour un seul tag
 * NOTE: Filtre automatiquement les tags exclus (date_sortie_inconnue)
 */
export const TagBadge = ({ tag, variant = "detail", showType = false }) => {
  // Filtrer les tags exclus
  if (!tag || EXCLUDED_TAGS.includes(tag)) return null;
  
  const getStyle = () => {
    return variant === "card" ? getTagStyleCard(tag) : getTagStyleDetail(tag);
  };

  return (
    <span
      className={`
        px-2 py-1 text-xs font-medium rounded-md border
        ${getStyle()}
      `}
      data-testid={`tag-badge-${tag}`}
      data-tag-type={showType ? (isSystemTag(tag) ? "auto" : isManualTag(tag) ? "manual" : "unknown") : undefined}
    >
      {getTagLabel(tag)}
    </span>
  );
};

/**
 * TagListSeparated - Affiche les tags AUTO et MANUAL séparément
 * Utile pour l'Admin pour voir la distinction
 * NOTE: Filtre automatiquement les tags exclus (date_sortie_inconnue)
 */
export const TagListSeparated = ({ product, className = "" }) => {
  let tags = getNormalizedTags(product);
  
  // Filtrer les tags exclus
  tags = tags.filter(tag => !EXCLUDED_TAGS.includes(tag));
  
  if (!tags.length) return null;
  
  const systemTags = tags.filter(isSystemTag);
  const manualTags = tags.filter(isManualTag);
  
  return (
    <div className={`space-y-2 ${className}`}>
      {systemTags.length > 0 && (
        <div>
          <span className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">
            Tags Auto (système)
          </span>
          <div className="flex flex-wrap gap-1.5">
            {systemTags.map((tag, idx) => (
              <TagBadge key={`sys-${tag}-${idx}`} tag={tag} variant="detail" />
            ))}
          </div>
        </div>
      )}
      
      {manualTags.length > 0 && (
        <div>
          <span className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">
            Tags Manuels (Admin)
          </span>
          <div className="flex flex-wrap gap-1.5">
            {manualTags.map((tag, idx) => (
              <TagBadge key={`man-${tag}-${idx}`} tag={tag} variant="detail" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagList;
